
import { Link } from 'react-router-dom';

export default function WinterMentalHealth() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Article Header */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-full flex items-center justify-center mr-4">
                <i className="ri-user-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-teal-600 font-semibold">ビースト君</p>
                <p className="text-gray-500 text-sm">2025年10月3日</p>
              </div>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">冬季のメンタルヘルス対策について</h1>
            <p className="text-xl text-gray-600">寒い季節に起こりやすい心の不調と、薬剤師が教える効果的な対策方法をご紹介します</p>
          </div>
          <div className="flex justify-center">
            <img 
              src="https://public.readdy.ai/ai/img_res/2b52cc82e26cf91ef064a987a80f3bba.jpg"
              alt="冬季のメンタルヘルス対策"
              className="w-full max-w-2xl h-80 object-cover object-top rounded-xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 mb-8">
              こんにちは、ビースト君です！冬になると「なんとなく気分が落ち込む」「やる気が出ない」という相談を受けることが増えます。
              今日は冬季に起こりやすいメンタルヘルスの問題と、薬剤師として皆さんにお伝えしたい対策方法について解説します。
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">冬季うつ病（季節性感情障害）とは</h2>
            
            <p className="text-gray-700 mb-6">
              冬季うつ病は、日照時間の短縮により体内時計が乱れ、セロトニンやメラトニンの分泌バランスが崩れることで起こります。
              筋トレでも規則正しいリズムが大切なように、心の健康にも生活リズムが重要な役割を果たします。
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">冬季うつ病の主な症状</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">😔 気分の症状</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 憂うつ感、悲しみ</li>
                    <li>• 興味や喜びの喪失</li>
                    <li>• 集中力の低下</li>
                    <li>• 自己否定的な思考</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">😴 身体の症状</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 過眠（いくら寝ても眠い）</li>
                    <li>• 食欲増進（特に炭水化物）</li>
                    <li>• 体重増加</li>
                    <li>• 疲労感、だるさ</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏠 行動の症状</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 外出を避ける</li>
                    <li>• 人との接触を避ける</li>
                    <li>• 活動量の低下</li>
                    <li>• 仕事や学業の能率低下</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">⏰ 時期的特徴</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 10月〜3月に症状出現</li>
                    <li>• 春になると自然に改善</li>
                    <li>• 毎年同じ時期に繰り返す</li>
                    <li>• 北欧や北日本で多い</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">光療法の効果と実践方法</h2>
            
            <p className="text-gray-700 mb-6">
              冬季うつ病の治療で最も効果的とされているのが光療法です。
              筋トレで適切な負荷をかけるように、適切な光の刺激を与えることで体内時計をリセットできます。
            </p>

            <div className="bg-yellow-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-yellow-800 mb-4">光療法の実践方法</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🌅 朝の光浴び</h4>
                  <p className="text-gray-600 text-sm mb-2">起床後30分以内に明るい光を浴びる（2500〜10000ルクス）</p>
                  <p className="text-xs text-gray-500">効果：体内時計のリセット、セロトニン分泌促進</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💡 光療法器具の使用</h4>
                  <p className="text-gray-600 text-sm mb-2">専用のライトボックスを30分〜2時間使用</p>
                  <p className="text-xs text-gray-500">注意：目から50cm程度離し、直視は避ける</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🚶 屋外での活動</h4>
                  <p className="text-gray-600 text-sm mb-2">曇りの日でも屋外の光は室内の10倍以上明るい</p>
                  <p className="text-xs text-gray-500">推奨：朝の散歩、ウィンドウショッピング</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏠 室内環境の改善</h4>
                  <p className="text-gray-600 text-sm mb-2">カーテンを開け、明るい照明を使用</p>
                  <p className="text-xs text-gray-500">工夫：白色LED照明、反射板の活用</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">栄養面でのサポート</h2>
            
            <p className="text-gray-700 mb-6">
              冬季うつ病では特定の栄養素が不足しがちです。薬剤師として、食事やサプリメントでのサポート方法をお伝えします。
              筋トレでプロテインが大切なように、心の健康にも適切な栄養が必要です。
            </p>

            <div className="bg-green-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-green-800 mb-4">重要な栄養素と食材</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🐟 オメガ3脂肪酸</h4>
                  <p className="text-gray-600 text-sm mb-2">脳の神経伝達物質の働きを改善</p>
                  <p className="text-xs text-gray-500">食材：青魚（サバ、イワシ、サンマ）、くるみ、亜麻仁油</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">☀️ ビタミンD</h4>
                  <p className="text-gray-600 text-sm mb-2">セロトニン合成に関与、免疫機能向上</p>
                  <p className="text-xs text-gray-500">食材：鮭、きのこ類、卵黄、サプリメント</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🧠 ビタミンB群</h4>
                  <p className="text-gray-600 text-sm mb-2">神経系の正常な機能維持</p>
                  <p className="text-xs text-gray-500">食材：豚肉、レバー、玄米、豆類、緑黄色野菜</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🍫 トリプトファン</h4>
                  <p className="text-gray-600 text-sm mb-2">セロトニンの原料となるアミノ酸</p>
                  <p className="text-xs text-gray-500">食材：バナナ、牛乳、チーズ、大豆製品、ナッツ</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">運動療法の効果</h2>
            
            <p className="text-gray-700 mb-6">
              運動は天然の抗うつ薬とも言われています。筋トレ愛好家として、冬季のメンタルヘルスに効果的な運動方法をご紹介します。
              寒い季節でも継続できる運動プログラムが重要です。
            </p>

            <div className="bg-orange-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-orange-800 mb-4">冬季におすすめの運動</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🏠 室内での筋力トレーニング</h4>
                  <p className="text-gray-600 text-sm mb-2">自重トレーニングやダンベル運動で基礎代謝向上</p>
                  <p className="text-xs text-gray-500">効果：エンドルフィン分泌、自己効力感の向上</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🧘 ヨガ・ストレッチ</h4>
                  <p className="text-gray-600 text-sm mb-2">リラクゼーション効果とストレス軽減</p>
                  <p className="text-xs text-gray-500">推奨：朝のサンサルテーション、夜のリストラティブヨガ</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🚶 ウォーキング</h4>
                  <p className="text-gray-600 text-sm mb-2">日光浴と有酸素運動の相乗効果</p>
                  <p className="text-xs text-gray-500">コツ：朝の時間帯、20-30分程度、無理のないペース</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💃 ダンス・エアロビクス</h4>
                  <p className="text-gray-600 text-sm mb-2">楽しみながら運動でき、継続しやすい</p>
                  <p className="text-xs text-gray-500">メリット：音楽によるリラックス効果、達成感</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">生活リズムの整え方</h2>
            
            <p className="text-gray-700 mb-6">
              冬季うつ病の改善には、規則正しい生活リズムが欠かせません。
              薬剤師として患者さんにお伝えしている、実践しやすい生活習慣改善法をご紹介します。
            </p>

            <div className="bg-purple-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">生活リズム改善のポイント</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">⏰ 睡眠リズム</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 毎日同じ時間に起床・就寝</li>
                    <li>• 起床後すぐに光を浴びる</li>
                    <li>• 夜は暖色系の照明に切り替え</li>
                    <li>• 就寝2時間前からスマホを控える</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🍽️ 食事リズム</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 3食を規則正しく摂取</li>
                    <li>• 朝食でタンパク質を意識</li>
                    <li>• 夕食は就寝3時間前まで</li>
                    <li>• 過度な糖質制限は避ける</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🌡️ 体温リズム</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 朝の軽い運動で体温上昇</li>
                    <li>• 入浴で体温調節</li>
                    <li>• 適度な室温維持（20-22℃）</li>
                    <li>• 冷え対策の徹底</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🧘 ストレス管理</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 深呼吸・瞑想の習慣</li>
                    <li>• 趣味の時間を確保</li>
                    <li>• 人との交流を大切に</li>
                    <li>• 完璧主義を避ける</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">薬物療法について</h2>
            
            <p className="text-gray-700 mb-6">
              生活習慣の改善だけでは症状が改善しない場合、薬物療法が検討されます。
              薬剤師として、冬季うつ病に使用される薬について正しい知識をお伝えします。
            </p>

            <div className="bg-red-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-red-800 mb-4">主な治療薬と注意点</h3>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">💊 SSRI（選択的セロトニン再取り込み阻害薬）</h4>
                  <p className="text-gray-600 text-sm mb-2">セロトニンの働きを改善し、うつ症状を軽減</p>
                  <p className="text-xs text-gray-500">注意：効果発現まで2-4週間、副作用（吐き気、眠気など）</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">🌙 メラトニン受容体作動薬</h4>
                  <p className="text-gray-600 text-sm mb-2">睡眠リズムの調整、体内時計の正常化</p>
                  <p className="text-xs text-gray-500">特徴：依存性が少ない、自然な眠気を促進</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">☀️ ビタミンD製剤</h4>
                  <p className="text-gray-600 text-sm mb-2">ビタミンD不足の補正、免疫機能改善</p>
                  <p className="text-xs text-gray-500">用法：医師の指示に従い、定期的な血中濃度測定が必要</p>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">家族・周囲のサポート</h2>
            
            <p className="text-gray-700 mb-6">
              冬季うつ病は本人だけでなく、家族や周囲の理解とサポートが重要です。
              筋トレでもトレーニングパートナーがいると頑張れるように、心の健康にも支えが必要です。
            </p>

            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">サポートのポイント</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">👂 理解と共感</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 症状を否定せず受け入れる</li>
                    <li>• 「気の持ちよう」と言わない</li>
                    <li>• 話を聞く時間を作る</li>
                    <li>• 小さな変化も認める</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🤝 具体的なサポート</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 一緒に散歩や運動をする</li>
                    <li>• 明るい場所での食事</li>
                    <li>• 家事の分担・協力</li>
                    <li>• 医療機関受診の付き添い</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">⚠️ 注意すべきサイン</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 自殺をほのめかす発言</li>
                    <li>• 極度の絶望感の表現</li>
                    <li>• 社会的引きこもりの悪化</li>
                    <li>• 食事・睡眠の著しい変化</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">🏥 専門機関への相談</h4>
                  <ul className="text-gray-600 text-sm space-y-1">
                    <li>• 精神科・心療内科</li>
                    <li>• かかりつけ医</li>
                    <li>• 薬剤師への相談</li>
                    <li>• 心の健康相談窓口</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">まとめ</h2>
            
            <p className="text-gray-700 mb-6">
              冬季のメンタルヘルス対策は、光療法、栄養管理、運動、生活リズムの改善が基本となります。
              症状が重い場合は、恥ずかしがらずに専門機関に相談することが大切です。
            </p>

            <div className="bg-teal-50 rounded-xl p-6">
              <h3 className="text-xl font-semibold text-teal-800 mb-4">ビースト君からのメッセージ</h3>
              <p className="text-gray-700">
                心の健康も体の健康と同じように、日々のケアが大切です。筋トレで体を鍛えるように、
                心も適切なケアで強くできます。一人で抱え込まず、周囲のサポートを受けながら、
                寒い冬を乗り越えていきましょう。困った時はいつでも薬剤師に相談してくださいね！💪❄️
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">関連記事</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Link to="/beast-blog/health-management" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20demonstrating%20exercise%20and%20health%20tips%2C%20cartoon%20illustration%20showing%20workout%20and%20wellness%20advice%2C%20friendly%20educational%20content%2C%20colorful%20gym%20and%20pharmacy%20background&width=400&height=250&seq=beast-health-management-related2-updated&orientation=landscape"
                alt="健康管理"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">薬剤師が教える筋トレと健康管理</h3>
                <p className="text-gray-600">薬学知識と筋トレ経験を活かした健康管理方法をお伝えします</p>
              </div>
            </Link>
            <Link to="/beast-blog/effective-medication" className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100 overflow-hidden cursor-pointer">
              <img 
                src="https://readdy.ai/api/search-image?query=Muscular%20anime%20pharmacist%20character%20with%20black%20two-block%20haircut%20explaining%20medicine%20timing%20and%20proper%20medication%20usage%2C%20educational%20illustration%20with%20clock%20and%20medicine%20schedule%2C%20friendly%20cartoon%20style%2C%20professional%20healthcare%20setting&width=400&height=250&seq=beast-medication-timing-related3-updated&orientation=landscape"
                alt="薬の効果的な飲み方"
                className="w-full h-48 object-cover object-top"
              />
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">薬の効果的な飲み方とタイミング</h3>
                <p className="text-gray-600">薬の効果を最大限に引き出すための正しい服用方法について解説します</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-support" className="hover:text-white cursor-pointer">健康サポート</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/otc" className="hover:text-white cursor-pointer">OTC医薬品</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リ소스</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/guidelines" className="hover:text-white cursor-pointer">疾患ガイドライン</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
